import { getSupabase, hasFriendsAccess, getArchivedLivestreams, trackLivestreamView } from './lib/supabase';
import type { Livestream } from './lib/types';

const loadingState = document.getElementById('loadingState')!;
const archiveGrid = document.getElementById('archiveGrid')!;
const noStreams = document.getElementById('noStreams')!;
const videoModal = document.getElementById('videoModal')!;
const modalVideoContainer = document.getElementById('modalVideoContainer')!;
const closeModalBtn = document.getElementById('closeModal')!;
const signOutBtn = document.getElementById('signOutBtn')!;

/**
 * Extract YouTube video ID from various URL formats
 */
function getYouTubeId(url: string): string | null {
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\?\/]+)/,
    /youtube\.com\/live\/([^&\?\/]+)/,
  ];

  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match) return match[1];
  }

  return null;
}

/**
 * Get YouTube thumbnail URL
 */
function getYouTubeThumbnail(videoId: string): string {
  return `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
}

/**
 * Format date for display
 */
function formatDate(dateString: string | null): string {
  if (!dateString) return 'Date unknown';

  const date = new Date(dateString);
  const options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  };
  return date.toLocaleDateString('en-US', options);
}

/**
 * Open video in modal
 */
function openVideoModal(livestream: Livestream) {
  const videoId = getYouTubeId(livestream.youtube_url);
  if (!videoId) return;

  modalVideoContainer.innerHTML = `
    <iframe
      src="https://www.youtube.com/embed/${videoId}?autoplay=1"
      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
      allowfullscreen
    ></iframe>
  `;

  videoModal.classList.add('show');

  // Track view
  trackLivestreamView(livestream.id);
}

/**
 * Close video modal
 */
function closeVideoModal() {
  videoModal.classList.remove('show');
  modalVideoContainer.innerHTML = '';
}

/**
 * Render archive grid
 */
function renderArchive(livestreams: Livestream[]) {
  if (livestreams.length === 0) {
    loadingState.style.display = 'none';
    noStreams.style.display = 'block';
    return;
  }

  archiveGrid.innerHTML = livestreams.map(stream => {
    const videoId = getYouTubeId(stream.youtube_url);
    const thumbnailUrl = videoId
      ? (stream.thumbnail_url || getYouTubeThumbnail(videoId))
      : null;

    const description = stream.description || '';
    const truncatedDesc = description.length > 100
      ? description.substring(0, 100) + '...'
      : description;

    return `
      <div class="stream-card" data-stream-id="${stream.id}">
        <div class="stream-thumbnail">
          ${thumbnailUrl ? `<img src="${thumbnailUrl}" alt="${stream.title}">` : ''}
          <div class="play-icon">▶</div>
        </div>
        <div class="stream-info">
          <h3 class="stream-title">${stream.title}</h3>
          ${truncatedDesc ? `<p class="stream-description">${truncatedDesc}</p>` : ''}
          <p class="stream-date">${formatDate(stream.ended_at)}</p>
        </div>
      </div>
    `;
  }).join('');

  // Add click handlers
  livestreams.forEach(stream => {
    const card = archiveGrid.querySelector(`[data-stream-id="${stream.id}"]`);
    if (card) {
      card.addEventListener('click', () => openVideoModal(stream));
    }
  });

  loadingState.style.display = 'none';
  archiveGrid.style.display = 'grid';
}

/**
 * Initialize archive
 */
async function init() {
  try {
    // Check authentication and Friends access
    const hasAccess = await hasFriendsAccess();
    if (!hasAccess) {
      window.location.href = '/friends/checkout.html';
      return;
    }

    // Get archived livestreams
    const livestreams = await getArchivedLivestreams();
    renderArchive(livestreams);

  } catch (error) {
    console.error('Archive init error:', error);
    alert('Failed to load archive. Please try refreshing the page.');
  }
}

// Modal close handlers
closeModalBtn.addEventListener('click', closeVideoModal);
videoModal.addEventListener('click', (e) => {
  if (e.target === videoModal) {
    closeVideoModal();
  }
});

// Escape key to close modal
document.addEventListener('keydown', (e) => {
  if (e.key === 'Escape' && videoModal.classList.contains('show')) {
    closeVideoModal();
  }
});

// Sign out handler
signOutBtn.addEventListener('click', async (e) => {
  e.preventDefault();
  const supabase = getSupabase();
  await supabase.auth.signOut();
  window.location.href = '/';
});

// Initialize on load
init();
